import { useState, useEffect, lazy, Suspense, useTransition, useRef, Component, ReactNode } from "react";
import { CrmLayout } from "@/components/layout/CrmLayout";
import { LoginScreen } from "@/components/auth/LoginScreen";
import { useAuth } from "@/hooks/useAuth";
import { GlobalFilterProvider } from "@/contexts/GlobalFilterContext";
import { useNavigationStack } from "@/contexts/NavigationStackContext";
import { Skeleton } from "@/components/ui/skeleton";
import { usePreloadSections } from "@/hooks/usePreloadSections";
import { getUserStorage, setUserStorage, removeUserStorage, USER_STORAGE_KEYS } from "@/lib/userStorage";
import { useIsMobile } from "@/hooks/use-mobile";

// Lazy load all sections for code splitting
const Dashboard = lazy(() => import("@/components/sections/Dashboard").then(m => ({ default: m.Dashboard })));
const Email = lazy(() => import("@/components/sections/Email").then(m => ({ default: m.Email })));
const ELearning = lazy(() => import("@/components/sections/ELearning"));
const Customers = lazy(() => import("@/components/sections/Customers").then(m => ({ default: m.Customers })));
const VertraegeContainer = lazy(() => import("@/components/sections/VertraegeContainer").then(m => ({ default: m.VertraegeContainer })));
const Leads = lazy(() => import("@/components/sections/Leads").then(m => ({ default: m.Leads })));
const Contacts = lazy(() => import("@/components/sections/Contacts").then(m => ({ default: m.Contacts })));
const Abrechnungen = lazy(() => import("@/components/sections/Abrechnungen").then(m => ({ default: m.Abrechnungen })));
const RabatteSideletter = lazy(() => import("@/components/sections/RabatteSideletter").then(m => ({ default: m.RabatteSideletter })));
const Produktanbieter = lazy(() => import("@/components/sections/Produktanbieter").then(m => ({ default: m.Produktanbieter })));
const ProduktanbieterDetail = lazy(() => import("@/components/sections/ProduktanbieterDetail").then(m => ({ default: m.ProduktanbieterDetail })));
const Vertriebspartner = lazy(() => import("@/components/sections/Vertriebspartner").then(m => ({ default: m.Vertriebspartner })));
const Webdialer = lazy(() => import("@/components/sections/Webdialer").then(m => ({ default: m.Webdialer })));
const Downloadcenter = lazy(() => import("@/components/sections/Downloadcenter").then(m => ({ default: m.Downloadcenter })));
const MeineAufgaben = lazy(() => import("@/components/sections/MeineAufgaben").then(m => ({ default: m.MeineAufgaben })));
const HilfeSupport = lazy(() => import("@/components/sections/HilfeSupport").then(m => ({ default: m.HilfeSupport })));
const Settings = lazy(() => import("@/components/sections/Settings").then(m => ({ default: m.Settings })));
const Buchhaltung = lazy(() => import("@/components/sections/Buchhaltung").then(m => ({ default: m.Buchhaltung })));
const Berichte = lazy(() => import("@/components/sections/Berichte").then(m => ({ default: m.Berichte })));
const Vorlagen = lazy(() => import("@/components/sections/Vorlagen").then(m => ({ default: m.Vorlagen })));

// Section loading skeleton
const SectionLoader = () => (
  <div className="space-y-4 p-4">
    <Skeleton className="h-12 w-full" />
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
    <Skeleton className="h-64 w-full" />
  </div>
);

// Delayed Suspense fallback to prevent flickering for fast loads
// Hidden on mobile for cleaner UX
const DelayedSuspenseFallback = () => {
  const [showLoader, setShowLoader] = useState(false);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const timeout = setTimeout(() => setShowLoader(true), 200);
    return () => clearTimeout(timeout);
  }, []);
  
  // Hide loader completely on mobile
  if (isMobile) return null;
  
  // Return empty div initially, show skeleton only after delay
  if (!showLoader) return <div className="min-h-[200px]" />;
  return <SectionLoader />;
};

// Error boundary for section rendering - resets when key (section) changes
class SectionErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode; resetKey?: string },
  { hasError: boolean }
> {
  state = { hasError: false };
  
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  
  static getDerivedStateFromProps(props: { resetKey?: string }, state: { hasError: boolean; lastResetKey?: string }) {
    // Reset error state when resetKey changes (e.g., section navigation)
    if (props.resetKey !== state.lastResetKey) {
      return { hasError: false, lastResetKey: props.resetKey };
    }
    return null;
  }
  
  componentDidCatch(error: Error) {
    console.error('Section render error:', error);
  }
  
  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
const Index = () => {
  const {
    isAuthenticated,
    isLoading,
    isPasswordRecovery,
    user,
    wasJustLoggedIn
  } = useAuth();
  
  const navigationStack = useNavigationStack();
  
  // Transition state for smooth section changes
  const [isPending, startSectionTransition] = useTransition();
  
  // Track visited sections to optimize transitions
  const visitedSections = useRef(new Set<string>(['dashboard']));
  
  // Preload sections after authentication for faster navigation
  usePreloadSections(user?.id);

  // Initialize state with placeholders - will be restored from localStorage in useEffect
  const [currentSection, setCurrentSection] = useState('dashboard');
  const [selectedGesellschaftId, setSelectedGesellschaftId] = useState<string | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [selectedGesellschaft, setSelectedGesellschaft] = useState<any | null>(null);
  const [initialSearchTerm, setInitialSearchTerm] = useState<string>('');
  const [settingsView, setSettingsView] = useState<string | undefined>(undefined);
  const [downloadcenterParams, setDownloadcenterParams] = useState<{
    gesellschaftId?: string;
    viewMode?: 'list' | 'tree';
    dokumentenart?: string; // Name, e.g., "Sideletter"
    folder?: string; // Category folder for deep-link navigation
  }>({});
  
  // Deep-link navigation states for global search
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [leadsViewMode, setLeadsViewMode] = useState<'list' | 'table' | 'kanban' | null>(null);
  const [selectedAufgabeId, setSelectedAufgabeId] = useState<string | null>(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [selectedAbrechnungId, setSelectedAbrechnungId] = useState<string | null>(null);
  const [selectedFremdvertragId, setSelectedFremdvertragId] = useState<string | null>(null);
  const [produktanbieterTab, setProduktanbieterTab] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [aufgabenTab, setAufgabenTab] = useState<string | null>(null);

  // Restore navigation state on page reload or enforce dashboard on fresh login
  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    
    // Bei frischem Login → Dashboard erzwingen
    if (wasJustLoggedIn) {
      setCurrentSection('dashboard');
      setSelectedGesellschaftId(null);
      setSelectedGesellschaft(null);
      // Clear localStorage für clean state
      removeUserStorage(user.id, USER_STORAGE_KEYS.CURRENT_SECTION);
      removeUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT_ID);
      removeUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT);
      return;
    }
    
    // Bei Page Reload → Letzte Section wiederherstellen
    const savedSection = getUserStorage<string>(user.id, USER_STORAGE_KEYS.CURRENT_SECTION);
    const savedGesellschaftId = getUserStorage<string>(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT_ID);
    const savedGesellschaft = getUserStorage<any>(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT);
    
    if (savedSection && savedSection !== 'dashboard') {
      setCurrentSection(savedSection);
    }
    
    if (savedGesellschaftId) {
      setSelectedGesellschaftId(savedGesellschaftId);
    }
    
    if (savedGesellschaft) {
      setSelectedGesellschaft(savedGesellschaft);
    }
  }, [user?.id, isAuthenticated, wasJustLoggedIn]);

  // Save section to localStorage whenever it changes
  const handleSectionChange = (
    section: string,
    params?: any,
    options?: { skipHistory?: boolean }
  ) => {
    // Check if section was previously loaded to optimize transitions
    const wasPreviouslyLoaded = visitedSections.current.has(section);

    if (wasPreviouslyLoaded) {
      // Smooth transition for cached sections
      startSectionTransition(() => {
        setCurrentSection(section);
      });
    } else {
      // Direct navigation for new sections (let Suspense handle loading)
      visitedSections.current.add(section);
      setCurrentSection(section);
    }

    if (user?.id) {
      setUserStorage(user.id, USER_STORAGE_KEYS.CURRENT_SECTION, section);
    }

    // Prefetch dashboard data when returning to dashboard
    if (section === 'dashboard' && user?.id) {
      import('@/lib/prefetchData').then(({ prefetchDashboardData }) => {
        const queryClient = (window as any).__queryClient;
        if (queryClient) {
          prefetchDashboardData(queryClient, user.id).catch(() => {
            // Silently fail
          });
        }
      });
    }

    // Push history state for section changes (except dashboard)
    // IMPORTANT: allow callers (e.g. hashchange handler) to skipHistory to avoid popstate/hashchange loops.
    if (!options?.skipHistory && section !== 'dashboard') {
      window.history.pushState({ section }, '', `/#${section}`);
    }

    // Handle searchTerm from global search
    console.log('[Index.tsx] handleSectionChange called:', { section, params, searchTerm: params?.searchTerm });
    if (params?.searchTerm) {
      console.log('[Index.tsx] Setting initialSearchTerm to:', params.searchTerm);
      setInitialSearchTerm(params.searchTerm);
    } else {
      console.log('[Index.tsx] Clearing initialSearchTerm');
      setInitialSearchTerm('');
    }
    
    // Handle settings view parameter (e.g., from "Mein Profil" dropdown)
    if (section === 'einstellungen' && params?.view) {
      setSettingsView(params.view);
    } else if (section !== 'einstellungen') {
      setSettingsView(undefined);
    }
    
    // Handle produktanbieter gesellschaft change
    if (section === 'produktanbieter' && params?.gesellschaftId && user?.id) {
      setSelectedGesellschaftId(params.gesellschaftId);
      setUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT_ID, params.gesellschaftId);
      // Clear the gesellschaft object as we'll reload it
      setSelectedGesellschaft(null);
      removeUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT);
    } else if (section !== 'produktanbieter' && user?.id) {
      // Reset selected gesellschaft when leaving produktanbieter section
      setSelectedGesellschaftId(null);
      setSelectedGesellschaft(null);
      removeUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT_ID);
      removeUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT);
    }
    
    // Handle downloadcenter params
    if (section === 'downloadcenter' && params) {
      setDownloadcenterParams(prev => ({
        ...prev,
        ...(params.gesellschaftId && { gesellschaftId: params.gesellschaftId }),
        ...(params.dokumentenart && { dokumentenart: params.dokumentenart }),
        ...(params.viewMode && { viewMode: params.viewMode }),
        ...(params.folder && { folder: params.folder })
      }));
    } else if (section !== 'downloadcenter') {
      setDownloadcenterParams({});
    }
    
    // Handle contract selection from global search
    if (section === 'contracts' && params?.selectedContractId) {
      setSelectedContractId(params.selectedContractId);
    } else if (section !== 'contracts') {
      setSelectedContractId(null);
    }
    
    // Handle Fremdvertrag selection from global search
    if (section === 'contracts' && params?.selectedFremdvertragId) {
      setSelectedFremdvertragId(params.selectedFremdvertragId);
    } else if (section !== 'contracts') {
      setSelectedFremdvertragId(null);
    }
    
    // Handle deep-link navigation from global search
    // Customer selection
    if (section === 'customers' && params?.customerId) {
      setSelectedCustomerId(params.customerId);
    } else if (section !== 'customers') {
      setSelectedCustomerId(null);
    }
    
    // Lead selection
    if (section === 'leads' && params?.leadId) {
      setSelectedLeadId(params.leadId);
    } else if (section !== 'leads') {
      setSelectedLeadId(null);
    }
    if (section === 'leads' && params?.viewMode) {
      setLeadsViewMode(params.viewMode);
    } else if (section !== 'leads') {
      setLeadsViewMode(null);
    }
    
    // Aufgabe selection and tab
    if (section === 'meine-aufgaben') {
      if (params?.aufgabeId) {
        setSelectedAufgabeId(params.aufgabeId);
      }
      if (params?.tab) {
        setAufgabenTab(params.tab);
      }
    } else {
      setSelectedAufgabeId(null);
      setAufgabenTab(null);
    }
    
    // Partner selection
    if (section === 'vertriebspartner' && params?.partnerId) {
      setSelectedPartnerId(params.partnerId);
    } else if (section !== 'vertriebspartner') {
      setSelectedPartnerId(null);
    }
    
    // Abrechnung selection (buchhaltung section)
    if (section === 'abrechnungen' && params?.abrechnungId) {
      setSelectedAbrechnungId(params.abrechnungId);
    } else if (section !== 'abrechnungen') {
      setSelectedAbrechnungId(null);
    }
    
    // Produktanbieter tab and highlight
    if (section === 'produktanbieter' && params?.tab) {
      setProduktanbieterTab(params.tab);
      setHighlightId(params.highlightId || null);
    } else if (section !== 'produktanbieter') {
      setProduktanbieterTab(null);
      setHighlightId(null);
    }
  };

  // Handle produktanbieter sub-navigation with back button
  useEffect(() => {
    if (selectedGesellschaftId && user?.id) {
      const id = navigationStack.push({
        type: 'sub-navigation',
        onClose: () => {
          setSelectedGesellschaftId(null);
          setSelectedGesellschaft(null);
          removeUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT_ID);
          removeUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT);
        }
      });
      
      return () => {
        navigationStack.remove(id);
      };
    }
  }, [selectedGesellschaftId, user?.id]);

  // Handle browser back button for section navigation
  // NOTE: NavigationStackContext already handles its own popstate for dialogs/sheets
  // This handler ONLY deals with section navigation when no overlays are active
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // If the event has a navigationStackId, it's being handled by NavigationStackContext
      // Don't interfere - let the context handle dialog/sheet closing
      if (event.state?.navigationStackId) {
        return;
      }
      
      // If navigation stack has entries, the NavigationStackContext handler will deal with it
      // We skip section navigation in this case
      if (navigationStack.hasEntries()) {
        return;
      }
      
      // Get target section from event state
      const section = event.state?.section;
      
      // Only trigger transition if we're actually changing sections
      // This prevents isPending from getting stuck when dialog closes trigger popstate
      // but we're staying on the same section
      if (section && section === currentSection) {
        // Same section, no need to transition - just ensure we don't get stuck in isPending
        return;
      }
      
      // Handle section navigation only when no overlays are active
      startSectionTransition(() => {
        if (section) {
          setCurrentSection(section);
          if (user?.id) {
            setUserStorage(user.id, USER_STORAGE_KEYS.CURRENT_SECTION, section);
          }
        } else {
          // Navigate to dashboard if no section in state
          setCurrentSection('dashboard');
          if (user?.id) {
            setUserStorage(user.id, USER_STORAGE_KEYS.CURRENT_SECTION, 'dashboard');
          }
        }
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigationStack, user?.id, currentSection]);

  // Handle URL hash navigation (e.g., from email links or deep links)
  useEffect(() => {
    if (!isAuthenticated || isPasswordRecovery) return;

    const handleHashChange = () => {
      const rawHash = window.location.hash.replace('#', '');
      if (!rawHash) return;

      const [section, queryString] = rawHash.split('?');
      const params = new URLSearchParams(queryString || '');

      // If this hash change was triggered by our own internal navigation (handleSectionChange pushing /#section),
      // the history state will already contain the target section. In that case, we only want to clean up
      // the URL hash (replaceState) and avoid re-triggering navigation/state updates.
      const stateSection = window.history.state?.section;
      if (stateSection === section) {
        window.history.replaceState(window.history.state, '', window.location.pathname);
        return;
      }

      // Deep link navigation (external links / manual hash edits):
      // use skipHistory: true to prevent pushState inside handleSectionChange (we'll replaceState below).

      if (section === 'abrechnungen') {
        handleSectionChange('abrechnungen', undefined, { skipHistory: true });
      }

      if (section === 'customers') {
        handleSectionChange('customers', undefined, { skipHistory: true });
      }

      if (section === 'contracts') {
        const vertragId = params.get('vertrag');
        if (vertragId) {
          setSelectedContractId(vertragId);
        }
        handleSectionChange('contracts', undefined, { skipHistory: true });
      }

      if (section === 'einstellungen') {
        const view = params.get('view') || undefined;
        handleSectionChange('einstellungen', view ? { view } : undefined, { skipHistory: true });
      }

      if (section === 'meine-aufgaben') {
        const tab = params.get('tab') || undefined;
        handleSectionChange('meine-aufgaben', tab ? { tab } : undefined, { skipHistory: true });
      }

      // Clear hash from URL after handling, but preserve the section in history state
      // so that browser back works correctly after dialog closes
      const nextState = { ...(window.history.state || {}), section };
      window.history.replaceState(nextState, '', window.location.pathname);
    };

    // Handle initial hash on mount
    handleHashChange();

    // Listen for hash changes (e.g., from Email module "E-Mail-Konten verwalten" button)
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isAuthenticated, isPasswordRecovery]);
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Laden...</div>
      </div>;
  }

  // Show login screen if not authenticated OR in password recovery mode
  if (!isAuthenticated || isPasswordRecovery) {
    return <LoginScreen />;
  }
  const renderSection = () => {
    switch (currentSection) {
      case "dashboard":
        return user?.id ? <Dashboard /> : <SectionLoader />;
      case "email":
        return <Email />;
      case "elearning":
        return <ELearning />;
      case "customers":
        return <Customers key={`customers-${initialSearchTerm}-${selectedCustomerId}`} initialSearchTerm={initialSearchTerm} selectedCustomerId={selectedCustomerId} />;
      case "contracts":
        return <VertraegeContainer key={`contracts-${initialSearchTerm}-${selectedFremdvertragId}`} selectedContractId={selectedContractId || undefined} selectedFremdvertragId={selectedFremdvertragId || undefined} initialSearchTerm={initialSearchTerm} />;
      case "berichte":
        return <Berichte />;
      case "vorlagen":
        return <Vorlagen />;
      case "leads":
        return <Leads key={`leads-${initialSearchTerm}-${selectedLeadId}-${leadsViewMode}`} initialSearchTerm={initialSearchTerm} selectedLeadId={selectedLeadId} initialViewMode={leadsViewMode || undefined} />;
      case "contacts":
        return <Contacts key={`contacts-${initialSearchTerm}`} initialSearchTerm={initialSearchTerm} />;
      case "abrechnungen":
        return <Abrechnungen key={`abrechnungen-${initialSearchTerm}-${selectedAbrechnungId}`} initialSearchTerm={initialSearchTerm} selectedAbrechnungId={selectedAbrechnungId} />;
      case "fremdvertraege":
        // Redirect to contracts tab with search term
        return <VertraegeContainer key={`fremd-${initialSearchTerm}-${selectedFremdvertragId}`} selectedContractId={undefined} selectedFremdvertragId={selectedFremdvertragId || undefined} initialSearchTerm={initialSearchTerm} />;
      case "rabatte-sideletter":
        return <RabatteSideletter />;
      case "produktanbieter":
        if (selectedGesellschaftId) {
          return <ProduktanbieterDetail 
            gesellschaftId={selectedGesellschaftId} 
            initialGesellschaft={selectedGesellschaft}
            initialTab={produktanbieterTab}
            highlightId={highlightId}
            onBack={() => {
              setSelectedGesellschaftId(null);
              setSelectedGesellschaft(null);
              setProduktanbieterTab(null);
              setHighlightId(null);
              if (user?.id) {
                removeUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT_ID);
                removeUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT);
              }
            }}
            onSectionChange={handleSectionChange} 
          />;
        }
        return <Produktanbieter 
          key={`produktanbieter-${initialSearchTerm}`}
          initialSearchTerm={initialSearchTerm}
          onGesellschaftSelect={(id, gesellschaft) => {
            setSelectedGesellschaftId(id);
            setSelectedGesellschaft(gesellschaft);
            if (user?.id) {
              setUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT_ID, id);
              setUserStorage(user.id, USER_STORAGE_KEYS.SELECTED_GESELLSCHAFT, gesellschaft);
            }
          }} 
          onSectionChange={handleSectionChange} 
        />;
      case "vertriebspartner":
        return <Vertriebspartner key={`vertriebspartner-${initialSearchTerm}-${selectedPartnerId}`} initialSearchTerm={initialSearchTerm} selectedPartnerId={selectedPartnerId} />;
      case "webdialer":
        return <Webdialer />;
      case "downloadcenter":
        return <Downloadcenter 
          key={`downloadcenter-${initialSearchTerm}-${downloadcenterParams.folder}`}
          initialGesellschaftId={downloadcenterParams.gesellschaftId} 
          initialViewMode={downloadcenterParams.viewMode} 
          initialDokumentenartName={downloadcenterParams.dokumentenart}
          initialSearchTerm={initialSearchTerm}
          initialFolder={downloadcenterParams.folder}
        />;
      case "meine-aufgaben":
        return <MeineAufgaben key={`meine-aufgaben-${initialSearchTerm}-${selectedAufgabeId}-${aufgabenTab}`} initialSearchTerm={initialSearchTerm} selectedAufgabeId={selectedAufgabeId} initialTab={aufgabenTab} />;
      case "buchhaltung":
        return <Buchhaltung />;
      case "hilfe-support":
        return <HilfeSupport />;
      case "einstellungen":
        return <Settings initialView={settingsView} />;
      default:
        return <Dashboard />;
    }
  };
  return <GlobalFilterProvider>
      <CrmLayout currentSection={currentSection} onSectionChange={handleSectionChange}>
        <div
          className={`flex flex-col transition-opacity duration-150 ${
            isPending ? 'opacity-70' : ''
          }`}
        >
          <SectionErrorBoundary fallback={<SectionLoader />} resetKey={currentSection}>
            <Suspense fallback={<DelayedSuspenseFallback />}>
              {renderSection()}
            </Suspense>
          </SectionErrorBoundary>
        </div>
      </CrmLayout>
    </GlobalFilterProvider>;
};
export default Index;